import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentOrg } from '../hooks/useCurrentOrg';
import { Icon, type IconName } from './Icon';

interface DashboardLayoutProps {
    children: ReactNode;
}

interface NavItem {
    to: string;
    label: string;
    icon: IconName;
    end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
    { to: '/dashboard', label: 'Mes générateurs', icon: 'grid', end: true },
    { to: '/leads', label: 'Mes demandes', icon: 'users' },
    { to: '/docs', label: 'Documentation', icon: 'book' },
    { to: '/api', label: 'API', icon: 'code' },
];

/**
 * Layout partagé pour toutes les pages authentifiées du dashboard.
 * Fournit la navbar avec navigation principale + menu utilisateur.
 */
export function DashboardLayout({ children }: DashboardLayoutProps) {
    const { user, logout } = useAuth();
    const { org, role } = useCurrentOrg();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const displayName = user?.displayName || user?.email?.split('@')[0] || 'toi';
    const initials = getInitials(user?.displayName, user?.email);

    // Fermer le menu utilisateur au clic extérieur + ESC
    useEffect(() => {
        if (!menuOpen) return;
        const onClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    const handleLogout = async () => {
        setMenuOpen(false);
        await logout();
    };

    const goTo = (path: string) => {
        setMenuOpen(false);
        navigate(path);
    };

    return (
        <div className="dashboard">
            <header className="dash-nav">
                <div className="dash-nav-inner">
                    <a href="/" className="dash-brand" aria-label="Quotio — Accueil">
                        <img src="/logoQuotio.png" alt="Quotio" />
                    </a>

                    <nav className="dash-nav-links" aria-label="Navigation principale">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `dash-nav-link${isActive ? ' is-active' : ''}`
                                }
                            >
                                <Icon name={item.icon} size={15} />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </nav>

                    <div className="dash-user" ref={menuRef}>
                        <button
                            type="button"
                            className="dash-user-btn"
                            aria-expanded={menuOpen}
                            aria-haspopup="menu"
                            onClick={() => setMenuOpen((v) => !v)}
                        >
                            {user?.photoURL ? (
                                <img src={user.photoURL} alt="" className="dash-avatar" />
                            ) : (
                                <span className="dash-avatar dash-avatar-fallback" aria-hidden>
                                    {initials}
                                </span>
                            )}
                            <span className="dash-user-name">{displayName}</span>
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                                className={`dash-user-chev${menuOpen ? ' is-open' : ''}`}
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </button>

                        {menuOpen && (
                            <div className="dash-menu" role="menu">
                                <div className="dash-menu-header">
                                    <strong>{displayName}</strong>
                                    <span className="dash-menu-email">{user?.email}</span>
                                    {org && (
                                        <span className="dash-menu-org">
                                            <span className="dash-org-dot" aria-hidden />
                                            {org.name}
                                            {role && <span className="dash-menu-role">· {role}</span>}
                                        </span>
                                    )}
                                </div>
                                <div className="dash-menu-sep" />
                                <button
                                    type="button"
                                    className="dash-menu-item"
                                    onClick={() => goTo('/account')}
                                    role="menuitem"
                                >
                                    <Icon name="user" size={15} />
                                    <span>Mon compte</span>
                                </button>
                                <button
                                    type="button"
                                    className="dash-menu-item"
                                    onClick={() => goTo('/docs')}
                                    role="menuitem"
                                >
                                    <Icon name="book" size={15} />
                                    <span>Documentation</span>
                                </button>
                                <div className="dash-menu-sep" />
                                <button
                                    type="button"
                                    className="dash-menu-item dash-menu-item-danger"
                                    onClick={handleLogout}
                                    role="menuitem"
                                >
                                    <Icon name="logout" size={15} />
                                    <span>Se déconnecter</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {children}

            <footer className="dash-footer" aria-label="Informations légales">
                <div className="dash-footer-inner">
                    <span className="dash-footer-copy">© 2026 Quotio</span>
                    <nav className="dash-footer-links" aria-label="Liens légaux">
                        <a href="/conditions-utilisation.html">Conditions d'utilisation</a>
                        <a href="/politique-confidentialite.html">Confidentialité</a>
                        <a href="/cookies.html">Cookies</a>
                        <a href="/mentions-legales.html">Mentions légales</a>
                    </nav>
                </div>
            </footer>
        </div>
    );
}

function getInitials(name?: string | null, email?: string | null): string {
    if (name && name.trim()) {
        const parts = name.trim().split(/\s+/);
        const first = parts[0]?.[0] ?? '';
        const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
        return (first + last).toUpperCase() || '?';
    }
    if (email) return email[0]?.toUpperCase() ?? '?';
    return '?';
}
